import {
  SignalCategorySchema,
  InternalNetworkAdEntityKindSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';

export const InternalNetworkDiscoveryPathRelationshipSchema = z.object({
  sourceObjectId: z.string(),
  targetObjectId: z.string(),
  relationshipType: z.string(),
});

export const InternalNetworkDiscoveryHopSchema = z.object({
  sourceObjectId: z.string().min(1),
  targetObjectId: z.string().min(1),
  relationshipType: z.string().min(1),
});

export const InternalNetworkDiscoveryPathRefSchema = z.union([
  z.literal('shortest'),
  z.object({
    kind: z.literal('alternate'),
    index: z.number().int().nonnegative(),
  }),
]);

export const InternalNetworkDiscoverySignalMetadataSchema = z.object({
  targetId: z.string().optional(),
  source: z.string().optional(),
  discoveryKey: z.string().optional(),
  pairKey: z.string().optional(),
  whyBestNow: z.string().optional(),
  hop: InternalNetworkDiscoveryHopSchema.optional(),
  pathStepIndex: z.number().int().nonnegative().optional(),
  pathRef: InternalNetworkDiscoveryPathRefSchema.optional(),
  crownJewelId: z.string().optional(),
  targetEntityKind: InternalNetworkAdEntityKindSchema.optional(),
  targetEntityId: z.string().optional(),
  sourceEntityKind: InternalNetworkAdEntityKindSchema.optional(),
  sourceDisplayName: z.string().optional(),
  targetDisplayName: z.string().optional(),
  sourceObjectId: z.string().optional(),
  targetObjectId: z.string().optional(),
  relationshipType: z.string().optional(),
  signalCategory: SignalCategorySchema.optional(),
  objectId: z.string().optional(),
  pathRelationshipCount: z.number().optional(),
  pathRelationships: z
    .array(InternalNetworkDiscoveryPathRelationshipSchema)
    .optional(),
  alternatePathCount: z.number().optional(),
  alternatePathRelationships: z
    .array(z.array(InternalNetworkDiscoveryPathRelationshipSchema))
    .optional(),
  confidence: z.number().int().min(1).max(100).optional(),
  confidenceEvidence: z
    .object({
      pathLength: z.number().int().nonnegative(),
      verifiedEntityCount: z.number().int().nonnegative(),
      totalEntityCount: z.number().int().nonnegative(),
      verifiedRelationshipCount: z.number().int().nonnegative(),
      totalRelationshipCount: z.number().int().nonnegative(),
      supportedRelationshipCount: z.number().int().nonnegative(),
      actionableRelationshipCount: z.number().int().nonnegative(),
      graphEvidenceCoverage: z.number().min(0).max(1),
      supportedEdgeCoverage: z.number().min(0).max(1),
      actionableEdgeCoverage: z.number().min(0).max(1),
    })
    .optional(),
  signalGenExecutionId: z.string().optional(),
});

export type InternalNetworkDiscoverySignalMetadata = z.infer<
  typeof InternalNetworkDiscoverySignalMetadataSchema
>;

export type InternalNetworkDiscoveryHop = z.infer<
  typeof InternalNetworkDiscoveryHopSchema
>;

export type InternalNetworkDiscoveryPathRef = z.infer<
  typeof InternalNetworkDiscoveryPathRefSchema
>;

export const InternalNetworkDiscoverySignalWriteMetadataSchema =
  InternalNetworkDiscoverySignalMetadataSchema.extend({
    discoveryKey: z.string().min(1),
    source: z.string().min(1),
    signalCategory: SignalCategorySchema,
    pairKey: z.string().min(1),
    whyBestNow: z.string().min(1),
    hop: InternalNetworkDiscoveryHopSchema,
  });

export type InternalNetworkDiscoverySignalWriteMetadata = z.infer<
  typeof InternalNetworkDiscoverySignalWriteMetadataSchema
>;

export const parseInternalNetworkDiscoverySignalMetadata = (
  metadata: unknown,
): InternalNetworkDiscoverySignalMetadata => {
  const parsed =
    InternalNetworkDiscoverySignalMetadataSchema.safeParse(metadata);

  return parsed.success ? parsed.data : {};
};

export const parseInternalNetworkDiscoverySignalWriteMetadata = (
  metadata: unknown,
): InternalNetworkDiscoverySignalWriteMetadata =>
  InternalNetworkDiscoverySignalWriteMetadataSchema.parse(metadata);
